// Crash-safe recharge persistence + recovery.
//
// Why this exists:
//   Razorpay's Checkout `handler` is an in-memory closure. If the PWA/WebView
//   is killed (Android low-memory, user backgrounds + system reclaims, OS swipe
//   away) AFTER the user pays but BEFORE we hit `/wallet/recharge/verify`,
//   the payment_id + signature are lost. The money has left the user's bank,
//   but the wallet was never credited.
//
//   We fix this by:
//     1) Persisting the order_id to localStorage BEFORE opening Razorpay.
//        Even if the entire app dies before the handler runs, the order_id
//        survives. On next launch we can ask the server to reconcile against
//        Razorpay's API.
//     2) Persisting the FULL signature triple as soon as the handler fires
//        — before the verify call — so a verify that's interrupted mid-flight
//        can be safely retried with exponential backoff.
//     3) Running a recovery pass on every app load (Wallet mount + post-login).

import { api } from "../api";

const KEY = "dt_pending_recharges";

function readQueue() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (_) {
    return [];
  }
}

function writeQueue(arr) {
  try { localStorage.setItem(KEY, JSON.stringify(arr)); } catch (_) { /* private mode */ }
}

/** Add an order to the pending queue right after order creation, before opening Razorpay. */
export function trackOrderCreated({ order_id, amount, user_id }) {
  if (!order_id) return;
  const q = readQueue().filter((x) => x.order_id !== order_id);
  q.push({
    order_id,
    amount,
    user_id,
    payment_id: null,
    signature: null,
    state: "created",       // created → signature_received → verifying → done
    created_at: new Date().toISOString(),
    attempts: 0,
  });
  writeQueue(q);
  console.info("[recharge] tracked", { order_id, amount });
}

/** Razorpay handler fired with success IDs — persist them BEFORE doing verify. */
export function trackSignatureReceived({ order_id, payment_id, signature }) {
  const q = readQueue();
  const idx = q.findIndex((x) => x.order_id === order_id);
  if (idx === -1) {
    q.push({
      order_id, payment_id, signature, state: "signature_received",
      created_at: new Date().toISOString(), attempts: 0,
    });
  } else {
    q[idx] = { ...q[idx], payment_id, signature, state: "signature_received" };
  }
  writeQueue(q);
  console.info("[recharge] signature persisted", { order_id, payment_id });
}

/** Mark an entry as done & remove from the queue. */
export function clearOrder(order_id) {
  const q = readQueue().filter((x) => x.order_id !== order_id);
  writeQueue(q);
  console.info("[recharge] cleared", { order_id });
}

/** Get currently pending entries (for UI display if ever needed). */
export function listPending() {
  return readQueue();
}

const MAX_ATTEMPTS = 6;
const BASE_DELAY_MS = 800;

function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }

async function verifyOne(entry) {
  return await api.post("/wallet/recharge/verify", {
    razorpay_order_id: entry.order_id,
    razorpay_payment_id: entry.payment_id,
    razorpay_signature: entry.signature,
  }, { timeout: 20000 });
}

async function reconcileOne(entry) {
  return await api.post("/wallet/recharge/reconcile", {
    razorpay_order_id: entry.order_id,
  }, { timeout: 25000 });
}

/** Retry a verify with exponential backoff. Returns axios response or throws. */
export async function verifyWithRetry(entry) {
  let lastErr;
  for (let i = 0; i < MAX_ATTEMPTS; i++) {
    try {
      console.info("[recharge] verify attempt", i + 1, "/", MAX_ATTEMPTS, entry.order_id);
      const r = await verifyOne(entry);
      return r;
    } catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      // 4xx (except 408/429) = permanent — don't retry.
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        console.warn("[recharge] verify permanent failure", status, e?.response?.data);
        throw e;
      }
      // Network / 5xx / timeout — retry with backoff.
      await sleep(BASE_DELAY_MS * Math.pow(2, i));
    }
  }
  throw lastErr;
}

/** Run the recovery pass — process every persisted order.
 * Returns array of { order_id, outcome, credited?, balance? } for callers
 * that want to show a toast / refresh UI.
 */
export async function runRechargeRecovery({ onCredited } = {}) {
  const q = readQueue();
  if (q.length === 0) return [];
  console.info("[recharge] recovery starting, pending:", q.length);

  const outcomes = [];
  for (const entry of q) {
    try {
      // Skip very fresh entries (< 3 s old) — the live flow may still be in progress.
      const ageMs = Date.now() - new Date(entry.created_at).getTime();
      if (ageMs < 3000 && entry.state === "created") {
        outcomes.push({ order_id: entry.order_id, outcome: "skipped_too_fresh" });
        continue;
      }

      let res;
      if (entry.state === "signature_received" && entry.payment_id && entry.signature) {
        // We have the full triple — preferred path (cheaper, no Razorpay API roundtrip).
        try {
          res = await verifyWithRetry(entry);
        } catch (e) {
          // verify failed permanently — fall through to reconcile so we still recover.
          console.warn("[recharge] verify exhausted; falling back to reconcile", e?.response?.data);
          res = await reconcileOne(entry);
        }
      } else {
        // No signature on file — only path is reconcile.
        res = await reconcileOne(entry);
      }

      const data = res?.data || {};
      if (data.credited && data.credited > 0) {
        onCredited?.({ amount: data.credited, balance: data.balance, order_id: entry.order_id });
      }
      // Treat any explicit success ("paid" or "already_processed") as done.
      if (data.success && (data.already_processed || data.credited > 0)) {
        clearOrder(entry.order_id);
        outcomes.push({ order_id: entry.order_id, outcome: "credited_or_already", ...data });
      } else {
        outcomes.push({ order_id: entry.order_id, outcome: "pending", ...data });
      }
    } catch (e) {
      const status = e?.response?.status;
      console.error("[recharge] recovery error", entry.order_id, status, e?.response?.data || e?.message);
      // Clear only on permanent 404 (order not found / unknown user) so we don't retry forever.
      if (status === 404) clearOrder(entry.order_id);
      outcomes.push({ order_id: entry.order_id, outcome: "error", status });
    }
  }
  return outcomes;
}

/** Defensive helper: never throw from inside the Razorpay handler. */
export function safe(fn) {
  return async function safeWrapped(...args) {
    try { return await fn(...args); }
    catch (e) { console.error("[recharge] handler safe-caught error:", e); }
  };
}
